import React, { useContext, useEffect, useRef } from 'react';
import { LanguageContext } from '../LanguageContext';

const VenueMap: React.FC = () => {
    const { language } = useContext(LanguageContext);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    
    // Bilingual text content for map labels
    const text = {
        ro: {
            danceFloor: "Ringul de dans",
            bar: "Bar",
            djBooth: "DJ",
            wc: "Toalete",
            logistica: "Logistică",
            tables: "Mese invitați",
            kidsCorner: "Colț pentru copii",
            entrance: "Intrare",
            cigarBar: "Zonă fumat",
            catering: "Catering"
        },
        en: {
            danceFloor: "Dance floor",
            bar: "Bar",
            djBooth: "DJ booth",
            wc: "WC",
            logistica: "Logistics",
            tables: "Guest tables",
            kidsCorner: "Kids corner",
            entrance: "Entrance",
            cigarBar: "Cigar bar area",
            catering: "Catering"
        }
    };
    
    const t = text[language];

    // Canvas setup and floor plan drawing
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size to full screen minus header height
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight - 80; // Subtract header height
        };

        // Initial sizing
        resizeCanvas();

        // Resize event listener
        window.addEventListener('resize', resizeCanvas);

        // Function to draw the floor plan
        const drawFloorPlan = () => {
            if (!ctx || !canvas) return;
            
            // Clear canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Background
            ctx.fillStyle = '#f5f1eb';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Add a semi-transparent overlay to make content less visible
            ctx.fillStyle = 'rgba(245, 241, 235, 0.6)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Calculate scale to fit the floor plan properly
            // The original dimensions seem to be roughly 41.54m × 21.42m based on the photo
            const originalWidth = 41.54;
            const originalHeight = 21.42;
            const aspectRatio = originalWidth / originalHeight;
            
            // Define margins for the drawing area
            const margin = Math.min(canvas.width, canvas.height) * 0.1;
            
            // Calculate the maximum size that fits while maintaining aspect ratio
            let drawWidth, drawHeight;
            if (canvas.width / canvas.height > aspectRatio) {
                // Canvas is wider than needed
                drawHeight = canvas.height - margin * 2;
                drawWidth = drawHeight * aspectRatio;
            } else {
                // Canvas is taller than needed
                drawWidth = canvas.width - margin * 2;
                drawHeight = drawWidth / aspectRatio;
            }
            
            // Calculate the top-left position to center the drawing
            const startX = (canvas.width - drawWidth) / 2;
            const startY = (canvas.height - drawHeight) / 2;
            
            // Scale factor to convert meters to pixels
            const scaleX = drawWidth / originalWidth;
            const scaleY = drawHeight / originalHeight;
            
            // Function to convert meter coordinates to pixel coordinates
            const toPixelX = (meter: number) => startX + meter * scaleX;
            const toPixelY = (meter: number) => startY + meter * scaleY;
            
            // Helper to draw rectangles based on meter dimensions
            const drawRect = (x: number, y: number, width: number, height: number, fill = false, fillColor = 'rgba(200, 200, 200, 0.3)') => {
                ctx.beginPath();
                ctx.rect(toPixelX(x), toPixelY(y), width * scaleX, height * scaleY);
                if (fill) {
                    ctx.fillStyle = fillColor;
                    ctx.fill();
                }
                ctx.stroke();
            };
            
            // Helper to draw circles
            const drawCircle = (x: number, y: number, radius: number, fill = false, fillColor = 'rgba(200, 200, 200, 0.2)') => {
                ctx.beginPath();
                ctx.arc(toPixelX(x), toPixelY(y), radius * scaleX, 0, Math.PI * 2);
                if (fill) {
                    ctx.fillStyle = fillColor;
                    ctx.fill();
                }
                ctx.stroke();
            };
            
            // Set the line styles for the floor plan - lighter color for WIP effect
            ctx.strokeStyle = '#aaa';
            ctx.lineWidth = Math.max(1, Math.min(canvas.width, canvas.height) / 300);
            ctx.lineJoin = 'round';
            
            // Draw the outer boundary of the venue
            ctx.beginPath();
            ctx.moveTo(toPixelX(0), toPixelY(0));
            ctx.lineTo(toPixelX(41.54), toPixelY(0));
            ctx.lineTo(toPixelX(41.54), toPixelY(21.42));
            ctx.lineTo(toPixelX(0), toPixelY(21.42));
            ctx.closePath();
            ctx.stroke();
            
            // Draw diagonal line at the entrance
            ctx.beginPath();
            ctx.moveTo(toPixelX(0), toPixelY(21.42));
            ctx.lineTo(toPixelX(21), toPixelY(21.42));
            ctx.stroke();
            
            ctx.setLineDash([5, 5]);
            ctx.beginPath();
            ctx.moveTo(toPixelX(0), toPixelY(17));
            ctx.lineTo(toPixelX(21), toPixelY(21.42));
            ctx.stroke();
            ctx.setLineDash([]);
            
            // Draw Dance Floor (center square with cross-hatch pattern)
            const danceX = 18;
            const danceY = 10;
            const danceSize = 6;
            
            // Dance floor outline
            drawRect(danceX, danceY, danceSize, danceSize);
            
            // Cross-hatch pattern
            ctx.strokeStyle = '#666';
            ctx.lineWidth = Math.max(0.5, Math.min(canvas.width, canvas.height) / 500);
            
            // Draw hatching
            const hatchSpacing = 0.3;
            for (let i = -danceSize; i <= danceSize; i += hatchSpacing) {
                ctx.beginPath();
                ctx.moveTo(toPixelX(danceX), toPixelY(danceY + danceSize + i));
                ctx.lineTo(toPixelX(danceX + danceSize), toPixelY(danceY + i));
                ctx.stroke();
                
                ctx.beginPath();
                ctx.moveTo(toPixelX(danceX), toPixelY(danceY + i));
                ctx.lineTo(toPixelX(danceX + danceSize), toPixelY(danceY + danceSize + i));
                ctx.stroke();
            }
            
            ctx.strokeStyle = '#333';
            ctx.lineWidth = Math.max(1, Math.min(canvas.width, canvas.height) / 300);
            
            // Draw DJ Booth
            drawRect(17, 8, 2, 1, true);
            
            // Draw bar area (top right)
            drawRect(34, 4, 5, 2, true);
            
            // Draw WC
            drawRect(30, 4, 2, 2, true);
            
            // Draw cigar bar area
            drawRect(7, 7, 5, 5, true);
            
            // Draw Logistics area (top right corner)
            drawRect(34, 0, 7, 3, true);
            
            // Draw Kids corner
            drawRect(4, 13, 6, 3, true);
            
            // Catering area (middle right)
            drawRect(28, 6, 4, 4, true);
            
            // Draw tables (bottom half)
            const tableRows = 3;
            const tableCols = 3;
            const tableWidth = 6;
            const tableHeight = 1;
            const tableSpacingX = 1;
            const tableSpacingY = 1;
            
            const tableStartX = 10;
            const tableStartY = 15;
            
            for (let row = 0; row < tableRows; row++) {
                for (let section = 0; section < 3; section++) {
                    const sectionOffset = section * (tableWidth + 2);
                    drawRect(
                        tableStartX + sectionOffset,
                        tableStartY + row * (tableHeight + tableSpacingY),
                        tableWidth,
                        tableHeight,
                        true,
                        'rgba(220, 200, 180, 0.4)'
                    );
                }
            }
            
            // Draw circles representing trees or decorative elements
            // Left side
            drawCircle(6, 18, 1.5, true);
            drawCircle(4, 11, 1.5, true);
            
            // Right side
            drawCircle(25, 14, 1.5, true);
            drawCircle(32, 14, 1.5, true);
            drawCircle(39, 14, 1.5, true);
            
            // Top area
            drawCircle(22, 5, 1.5, true);
            
            // Draw text labels - lighter for WIP effect
            ctx.font = `${Math.max(14, Math.min(canvas.width, canvas.height) / 40)}px serif`;
            ctx.fillStyle = '#999';
            ctx.textAlign = 'center';
            
            // Dance floor label
            ctx.fillText(t.danceFloor, toPixelX(danceX + danceSize/2), toPixelY(danceY + danceSize/2));
            
            // DJ booth label
            ctx.fillText(t.djBooth, toPixelX(18), toPixelY(7.5));
            
            // Bar label
            ctx.fillText(t.bar, toPixelX(36.5), toPixelY(5));
            
            // WC label
            ctx.fillText(t.wc, toPixelX(31), toPixelY(5));
            
            // Logistica label
            ctx.fillText(t.logistica, toPixelX(37.5), toPixelY(1.5));
            
            // Tables label
            ctx.fillText(t.tables, toPixelX(22), toPixelY(18));
            
            // Kids corner label
            ctx.fillText(t.kidsCorner, toPixelX(7), toPixelY(14.5));
            
            // Entrance label (rotated)
            ctx.save();
            ctx.translate(toPixelX(5), toPixelY(19.5));
            ctx.rotate(-Math.PI / 6);
            ctx.fillText(t.entrance, 0, 0);
            ctx.restore();
            
            // Cigar bar area label
            ctx.fillText(t.cigarBar, toPixelX(9.5), toPixelY(9.5));
            
            // Catering label
            ctx.fillText(t.catering, toPixelX(30), toPixelY(8));
            
            // Draw a compass in the bottom right corner
            const compassX = canvas.width - margin / 2;
            const compassY = canvas.height - margin / 2;
            const compassSize = Math.min(canvas.width, canvas.height) / 15;
            
            // Compass circle
            ctx.beginPath();
            ctx.arc(compassX, compassY, compassSize, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.fill();
            ctx.stroke();
            
            // Compass directions
            ctx.font = `${compassSize/3}px serif`;
            ctx.fillStyle = '#444';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('N', compassX, compassY - compassSize * 0.7);
            ctx.fillText('S', compassX, compassY + compassSize * 0.7);
            ctx.fillText('E', compassX + compassSize * 0.7, compassY);
            ctx.fillText('W', compassX - compassSize * 0.7, compassY);
            
            // Compass needle
            ctx.beginPath();
            ctx.moveTo(compassX, compassY - compassSize * 0.6);
            ctx.lineTo(compassX + compassSize * 0.1, compassY);
            ctx.lineTo(compassX, compassY + compassSize * 0.6);
            ctx.lineTo(compassX - compassSize * 0.1, compassY);
            ctx.closePath();
            ctx.fillStyle = '#d32f2f';
            ctx.fill();
            
            // Title
            const titleFontSize = Math.min(canvas.width, canvas.height) / 20;
            ctx.font = `${titleFontSize}px serif`;
            ctx.fillStyle = '#222';
            ctx.textAlign = 'center';
            ctx.fillText(language === 'ro' ? 'Harta Locației' : 'Venue Map', canvas.width / 2, startY - titleFontSize);
            
            // Add WORK IN-PROGRESS overlay
            const wip_fontSize = Math.min(canvas.width, canvas.height) / 10;
            ctx.font = `bold ${wip_fontSize}px sans-serif`;
            
            // Add shadow for better visibility
            ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
            ctx.shadowBlur = 15;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            
            // Draw WIP text
            ctx.fillStyle = 'rgba(220, 60, 60, 0.8)';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(language === 'ro' ? 'ÎN LUCRU' : 'WORK IN-PROGRESS', canvas.width / 2, canvas.height / 2);
            
            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            
            // Scale indicator
            const scaleBarLength = 5; // 5 meters
            const scaleBarX = startX;
            const scaleBarY = canvas.height - margin / 3;
            const scaleBarHeight = margin / 20;
            
            ctx.fillStyle = '#333';
            ctx.fillRect(scaleBarX, scaleBarY, scaleBarLength * scaleX, scaleBarHeight);
            
            ctx.font = `${margin/5}px sans-serif`;
            ctx.fillText(`5m`, scaleBarX + (scaleBarLength * scaleX) / 2, scaleBarY + scaleBarHeight * 4);
        };
        
        // Initial draw
        drawFloorPlan();
        
        // Redraw on window resize
        window.addEventListener('resize', drawFloorPlan);
        
        // Add event listener for language changes to update labels
        const languageObserver = new MutationObserver(() => {
            drawFloorPlan();
        });
        
        // Cleanup
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('resize', drawFloorPlan);
            languageObserver.disconnect();
        };
    }, [language, t]);
    
    return (
        <div className="relative min-h-screen overflow-hidden">
            {/* Canvas as the only content (positioned right below header) */}
            <canvas 
                ref={canvasRef} 
                className="fixed top-0 left-0 w-full h-screen" 
                style={{ marginTop: '80px' }} // Offset for header height
            />
        </div>
    );
};

export default VenueMap;